import { DynamicContainer, Form, FormSection, Placeholder } from '@hai/ui-react';
import { Formik, FormikProps } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClientWithoutHandler } from 'src/js/api';
import { APIPath } from 'src/js/api/route-path-index';
import { useMutable } from 'src/js/hook/use-mutable';

//import { Loading } from 'src/js/component/loading';
import { ServicesViewModel } from './services-view-model';
import { StateButton } from 'src/js/component/base/state-button';
import { FormBuilder, FormFieldType } from 'src/js/component/form-builder';
import { FormikRadios } from 'src/js/component/base/form/formik-radios';
import nmosService, { saveResponseToNmosService, updateNmos, validateNmosAddress } from './nmos-store';
import { mapNmosServices, NmosServiceResponse, nmosValidationSchema, NmosViewModel } from './nmos-models';
import { constant } from 'src/js/constant';
import { validateFormContext } from 'src/js/validator/validator';
import { changes } from 'src/js/util/formik-helper';

interface Props {
  services: ServicesViewModel /* loading animation could be displayed while services.nic?.ems === undefined */;
}

export const NmosForm: React.FunctionComponent<Props> = () => {
  const { t } = useTranslation();
  const pollingInterval = constant.defaultRefreshInterval * 1;
  const [loading, setLoading] = useMutable(true);
  const [submitting, setSubmitting] = useState(false);
  const [model, setModel] = useState({ ...nmosService });

  const [pollingTimeout] = useMutable(pollingInterval);
  const [emsFetchTimeoutId, setEmsFetchTimeoutId] = useMutable(null);
  const formRef = useRef<FormikProps<NmosViewModel>>(null);

  let endrun = false;
  useEffect(() => {
    return () => {
      endrun = true;
    };
  }, []);

  // TODO try adaptiveFetch
  const fetchStatusFunc = () => {
    if (endrun) {
      // console.error('count/endrun', count, endrun);
      return; // stop
    }

    apiClientWithoutHandler.genericController.get<NmosServiceResponse>(APIPath.services.nmos).then((res: any) => {
      if (endrun) {
        // console.error('CALLBACK fetch, all instances stopped, aborting', uuid);
        // we got destroyed, abort
        return;
      }

      const data = res?.data?.data;
      if (res?.ok && data) {
        const response = mapNmosServices(data);
        saveResponseToNmosService(response);
        if (!formRef.current?.dirty) {
          setModel({ ...nmosService });
        }

        if (loading() && formRef.current) {
          // if we want to set some fields and turn of enableReinitialize :
          // formRef.current.setFieldValue('xyz', response.xyz, false);
          setLoading(false);
        }
      }

      if (!endrun) {
        const timeoutVal = pollingTimeout();
        // console.log('renewing fetch', timeoutVal);
        setEmsFetchTimeoutId(setTimeout(fetchStatusFunc, timeoutVal));
      } else {
        // console.error('not renewing fetch')
      }
    });
  };

  // just run once or when changing interval
  useEffect(() => {
    if (emsFetchTimeoutId() === null) {
      // console.log('triggerFetch because we have null ID')
      setEmsFetchTimeoutId(setTimeout(fetchStatusFunc, 0));
    }
  }, [emsFetchTimeoutId]);

  const PlaceHolderPage = () => {
    return (
      <>
        <div style={{ width: '300px', height: '40px', marginTop: '0px', marginBottom: '0px', float: 'left' }}>
          <Placeholder as="layout" />
        </div>
        <div className="clearfix"></div>
        <div style={{ width: '100%', height: '80px', marginTop: '30px', marginBottom: '30px', clear: 'both' }}>
          <Placeholder as="layout" />
        </div>
      </>
    );
  };

  // console.log('Formik init values', model);
  return (
    <Formik
      className="hai-mb-6"
      initialValues={{ ...model }} // FIXME display loading while uninitialized
      enableReinitialize={true}
      onSubmit={async (formValues: NmosViewModel) => {
        /*if (Object.keys(formRef.current.errors).length > 0) {
           console.log('errors', formRef.current.errors);
          return;
        }*/

        if (formValues.mode === 'manual') {
          const res = await validateNmosAddress(formValues.is04Address, t);
          if (res?.ok && res?.data?.error === true) {
            const fieldName = 'is04Address';
            const message = t('validation.network.ipv4');
            formRef.current.setErrors({ [fieldName]: message });
            return;
          }

          if (formValues.is09) {
            const res = await validateNmosAddress(formValues.is09Address, t);
            if (res?.ok && res?.data?.error === true) {
              const fieldName = 'is09Address';
              const message = t('validation.network.ipv4');
              formRef.current.setErrors({ [fieldName]: message });
              return;
            }
          }
        }

        // Replace existing relay
        updateNmos(formValues, t).then((res) => {
          if (res.ok) {
            setModel(mapNmosServices(res.data?.data));
          }
          setSubmitting(false);
        });
      }}
      innerRef={formRef}
      validationSchema={nmosValidationSchema(t)}
    >
      {(formikProps) => {
        const values = formikProps.values;

        let applyButtonEnabled = formikProps.dirty || !formikProps.isSubmitting || !formikProps.isValidating;
        const changed = changes(values, formikProps.initialValues);
        // console.log('changes', formikProps.dirty, changed)
        if (values.mode === 'automatic') {
          delete changed.is04Port;
          delete changed.is04Address;
          delete changed.is09;
          delete changed.is09Address;
          delete changed.is09Port;
        } else {
          // manual
          if (values.is09 === false) {
            delete changed.is09Address;
            delete changed.is09Port;
          }
        }
        if (Object.keys(changed).length === 0 || formikProps.isSubmitting || formikProps.isValidating) {
          applyButtonEnabled = false;
        }

        return (
          <Form className="hai-mb-6" {...{ id: 'nmos-form' }}>
            <FormSection title={t('settings.services.nmos.title')}>
              <>
                <div className="d-flex floatRight breakAfter">
                  <StateButton
                    variant="primary"
                    disabled={!applyButtonEnabled}
                    onClick={(): void => {
                      //   setSubmitting(true);
                      //    formikProps.setErrors({})
                      formikProps.setTouched(
                        { nodePort: true, is04Port: true, is04Address: true, is09Address: true, is09Port: true },
                        false,
                      );
                      /*
                      if (Object.keys(formikProps.errors).length > 0) {
                        console.log('onClick errors', formikProps.errors);
                      }*/
                      //submitAndValidate(formikProps, t) ;//TODO find why this doesnt work, it cause the wrong values to be displayed after
                      validateFormContext(formikProps, t);
                    }}
                    isLoading={submitting}
                  >
                    {t('settings.services.nmos.button')}
                  </StateButton>
                </div>
              </>
              {loading() ? (
                <PlaceHolderPage />
              ) : (
                <>
                  <DynamicContainer minColumns={3}>
                    <FormikRadios
                      name="mode"
                      label={t('settings.services.nmos.discoveryMode')}
                      radios={[
                        { value: 'automatic', label: t('settings.services.nmos.discoveryOptions.automatic') },
                        { value: 'manual', label: t('settings.services.nmos.discoveryOptions.manual') },
                      ]}
                    />
                    <div />
                  </DynamicContainer>
                  <DynamicContainer minColumns={3}>
                    <FormBuilder
                      mode="formik"
                      loading={loading()}
                      translationPrefixLabels="settings.services.nmos.field"
                      translationPrefixEnums="settings.services.nmos.enums"
                      fields={[
                        {
                          type: FormFieldType.INPUT_NUMBER,
                          name: 'nodePort',
                          placeholder: '8001',
                          hint: t('settings.services.nmos.field.nodePortHint'),
                        },
                      ]}
                    />
                  </DynamicContainer>
                  {values.mode === 'manual' && (
                    <div className="mk-sunken-section">
                      <DynamicContainer minColumns={3}>
                        <FormBuilder
                          mode="formik"
                          loading={loading()}
                          translationPrefixLabels="settings.services.nmos.field"
                          translationPrefixEnums="settings.services.nmos.enums"
                          fields={[
                            {
                              type: FormFieldType.SUBTITLE,
                              label: t('settings.services.nmos.is04title'),
                            },
                            {
                              type: FormFieldType.INPUT_TEXT,
                              name: 'is04Address',
                              required: true,
                            },
                            {
                              type: FormFieldType.INPUT_NUMBER,
                              name: 'is04Port',
                              required: true,
                              hint: t('settings.services.nmos.field.is04PortHint'),
                            },
                            {
                              type: FormFieldType.LINE_BREAK, // FIXME LINE_BREAK before checkbox doesnt work, needing breakBefore below
                            },
                            {
                              type: FormFieldType.CHECKBOX,
                              name: 'is09',
                              breakBefore: true,
                            },
                            {
                              type: FormFieldType.LINE_BREAK, // FIXME LINE_BREAK before checkbox doesnt work, needing breakBefore below
                            },
                            {
                              type: FormFieldType.INPUT_TEXT,
                              name: 'is09Address',
                              when: values.is09 === true,
                              required: true,
                              breakBefore: true,
                            },
                            {
                              type: FormFieldType.INPUT_NUMBER,
                              name: 'is09Port',
                              when: values.is09 === true,
                              required: true,
                              hint: t('settings.services.nmos.field.is09PortHint'),
                            },
                          ]}
                        />
                      </DynamicContainer>
                    </div>
                  )}
                </>
              )}
            </FormSection>
          </Form>
        );
      }}
    </Formik>
  );
};
